/**
 * Pending Mutations Sync Service
 *
 * Replays queued API mutations (shelter check-ins, PDA zone ops, etc.)
 * that were saved to IndexedDB when the network was unavailable.
 * Processes mutations in FIFO order with retry limits.
 */

import { db, PendingMutation } from './db';
import { api } from '@/services/api';
import { networkService } from '@/services/network.service';
import { useTenantStore } from '@/store/tenant.store';
import { isAlreadyAppliedReplay, originOnly, mustDeferSend, sendTenantOf } from './client-id';

const MAX_RETRIES = 5;

export { isAlreadyAppliedReplay };

/**
 * Ids currently being replayed **in this browser session**.
 *
 * WHY THIS IS IN MEMORY AND NOT IN INDEXEDDB:
 * In-flight state must die with the process. This queue previously persisted
 * `status: 'syncing'` to IndexedDB before awaiting the HTTP call, and `syncAll`
 * / `getPendingCount` only ever select `['pending','failed']`. If the page died
 * mid-request — an iOS/Android suspend (main.tsx fires syncNow() on the
 * *background* app-state transition), the user killing the app, a tab reload, or
 * the service worker's own auto-update reload — the row was stranded at
 * 'syncing' forever: never retried, never counted, and the sync pill reported
 * "Synced". A responder's completed PDA zone stayed unsearched on the EOC board
 * and a checked-out family kept counting against shelter occupancy, with zero
 * indication anything was queued. A crash must leave the row at 'pending' so the
 * next sync picks it up; only this in-memory set suppresses a concurrent replay,
 * and it is naturally empty after a reload.
 *
 * The concurrency it guards is real: useOfflineCache.ts calls
 * pendingMutationsSyncService.syncAll() directly, bypassing
 * syncOrchestratorService's inFlight guard, so two syncAll() runs can overlap.
 */
const inFlightIds = new Set<string>();

/**
 * Read the currently-active tenant id (or null if none / SSR).
 */
function getActiveTenantId(): string | null {
  try {
    return useTenantStore.getState().activeTenant?.id ?? null;
  } catch {
    return null;
  }
}

export interface MutationSyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
}

class PendingMutationsSyncService {
  /**
   * Enqueue a mutation for later replay, stamping the active tenant_id so it
   * can never be replayed against a different tenant after a switch.
   */
  async enqueue(
    mutation: Omit<PendingMutation, 'id' | 'createdAt' | 'retryCount' | 'status' | 'tenant_id'> &
      Partial<Pick<PendingMutation, 'createdAt' | 'retryCount' | 'status' | 'tenant_id'>>
  ): Promise<string> {
    const record: PendingMutation = {
      createdAt: Date.now(),
      retryCount: 0,
      status: 'pending',
      tenant_id: getActiveTenantId(),
      ...originOnly(mutation.tenant_id ?? getActiveTenantId()),
      ...mutation,
    };
    return db.pendingMutations.add(record);
  }

  /**
   * Replay all pending mutations in order (FIFO).
   * Marks each as synced on success, increments retryCount on failure.
   * Mutations that exceed MAX_RETRIES are marked 'failed' and skipped.
   */
  async syncAll(): Promise<MutationSyncResult> {
    const result: MutationSyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    if (!networkService.isOnline()) {
      result.success = false;
      result.errors.push('No network connection');
      return result;
    }

    const activeTenantId = getActiveTenantId();

    const pending = await db.pendingMutations
      .where('status')
      .anyOf(['pending', 'failed'])
      .sortBy('createdAt');

    for (const mutation of pending) {
      // Already being replayed by an overlapping syncAll() — skip, or we would
      // send the request twice (e.g. a duplicate POST /residents/:id/checkout).
      if (mutation.id != null && inFlightIds.has(String(mutation.id))) {
        continue;
      }

      if (mutation.retryCount >= MAX_RETRIES) {
        // Skip permanently failed mutations
        result.failed++;
        result.errors.push(`Mutation to ${mutation.endpoint} exceeded max retries`);
        continue;
      }

      // Tenant guard: never replay a mutation against a different tenant than the
      // one it was created under. The api client injects the *ambient* X-Tenant-ID,
      // so a Tenant-A mutation replayed while Tenant B is active would POST to B.
      // Defer (leave queued) instead of replaying; it will sync when A is active again.
      if (mutation.tenant_id != null && activeTenantId != null && mutation.tenant_id !== activeTenantId) {
        continue;
      }
      // Never replayed as someone else: another user's queued change waits for
      // them (or goes with the drain's handoff, with its own originator).
      if (mustDeferSend(mutation)) continue;

      const success = await this.replayMutation(mutation);
      if (success) {
        result.synced++;
      } else {
        result.failed++;
        result.errors.push(`Failed to sync ${mutation.method} ${mutation.endpoint}`);
      }
    }

    result.success = result.failed === 0;
    return result;
  }

  /**
   * Get count of pending mutations.
   */
  async getPendingCount(): Promise<number> {
    // 'syncing' is included so a row stranded by an older app version (or any
    // future writer of that state) is reported as still-pending work instead of
    // silently vanishing from the count while the pill claims "Synced".
    return db.pendingMutations
      .where('status')
      .anyOf(['pending', 'failed', 'syncing'])
      .filter(m => m.retryCount < MAX_RETRIES)
      .count();
  }

  /**
   * Crash recovery: return rows stranded at 'syncing' by a session that died
   * mid-replay to 'pending' so they are picked up by the next syncAll().
   *
   * Idempotent and safe to call repeatedly. Called once from
   * syncOrchestratorService.init() before the network listener is registered.
   *
   * REPLAY SAFETY: reconciliation can replay a mutation the server already
   * committed (the request may have reached the server before the process died).
   * Every current enqueue site is idempotent — the PDA zone calls are
   * field-setting PATCHes and shelter checkout is a state set
   * (shelter.service.ts checkOutResident sets status='checked_out'). Any FUTURE
   * non-idempotent enqueue (a POST that creates a row) must carry a
   * client-supplied idempotency key before relying on this.
   */
  async reconcileStale(): Promise<number> {
    return db.pendingMutations
      .where('status')
      .equals('syncing')
      .modify({ status: 'pending' });
  }

  /**
   * Replay a single mutation. Returns true on success.
   */
  private async replayMutation(mutation: PendingMutation): Promise<boolean> {
    // Contract 9 gate (client-id.ts mustDeferSend): never sent as someone else.
    if (mustDeferSend(mutation)) return false;
    const id = mutation.id!;
    const key = String(id);

    // In-flight is tracked in memory only. Persisting 'syncing' here is what
    // stranded mutations forever when the page died mid-request (see the
    // inFlightIds comment above) — the row must stay 'pending' on disk so a
    // crash leaves it retryable.
    inFlightIds.add(key);

    try {
      // Replay the API call
      switch (mutation.method) {
        case 'POST':
          await api.post(mutation.endpoint, mutation.body, sendTenantOf(mutation));
          break;
        case 'PUT':
          await api.put(mutation.endpoint, mutation.body, sendTenantOf(mutation));
          break;
        case 'PATCH':
          await api.patch(mutation.endpoint, mutation.body, sendTenantOf(mutation));
          break;
        case 'DELETE':
          await api.delete(mutation.endpoint, undefined, sendTenantOf(mutation));
          break;
      }

      // Success — remove from queue
      await db.pendingMutations.delete(id);
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';

      // P2-05: the queued change is already in effect on the server, so the
      // replay succeeded in substance. A shelter check-out replayed after its
      // first attempt landed (response lost, app killed mid-request, or the
      // resident checked out from the EOC meanwhile) answers
      // 409 ALREADY_CHECKED_OUT. That used to be a permanent failure: the row
      // sat in the queue as "failed" forever and the sync pill stayed red.
      if (isAlreadyAppliedReplay(error)) {
        await db.pendingMutations.delete(id);
        return true;
      }

      // Check if this is a 4xx client error (don't retry — the request is invalid)
      const status = (error as any)?.response?.status;
      if (status && status >= 400 && status < 500) {
        await db.pendingMutations.update(id, {
          status: 'failed',
          retryCount: MAX_RETRIES, // Mark as permanently failed
          errorMessage: `${status}: ${message}`,
        });
        console.warn(`[PendingMutations] Permanent failure for ${mutation.method} ${mutation.endpoint}: ${status}`);
      } else {
        // Network/server error — increment retry count
        await db.pendingMutations.update(id, {
          status: 'failed',
          retryCount: (mutation.retryCount || 0) + 1,
          errorMessage: message,
        });
      }

      return false;
    } finally {
      inFlightIds.delete(key);
    }
  }

  /**
   * Clear all permanently failed mutations (retryCount >= MAX_RETRIES).
   */
  async clearFailed(): Promise<number> {
    const failed = await db.pendingMutations
      .filter(m => m.retryCount >= MAX_RETRIES)
      .toArray();
    const ids = failed.map(m => m.id!);
    await db.pendingMutations.bulkDelete(ids);
    return ids.length;
  }
}

export const pendingMutationsSyncService = new PendingMutationsSyncService();
